/**
 * Workspace context global (WEB-11).
 *
 * Mantiene en memoria el "workspace activo" del user — el sidebar
 * muestra un dropdown con todos los workspaces visibles, el que él
 * seleccione queda guardado en localStorage para persistir entre F5.
 *
 * Las pages que dependen del workspace (Projects, Environments) leen
 * `activeWorkspaceId` y filtran sus queries con ese valor.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { listWorkspaces } from "../api/workspaces";
import type { Workspace } from "../api/types";

export const LS_KEY = "sar.activeWorkspaceId";

interface WorkspaceContextValue {
  workspaces: Workspace[];
  isLoading: boolean;
  activeWorkspaceId: string | null;
  activeWorkspace: Workspace | null;
  setActiveWorkspace: (id: string | null) => void;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { data, isLoading } = useQuery({
    queryKey: ["workspaces"],
    queryFn: listWorkspaces,
  });

  const [activeId, setActiveId] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(LS_KEY);
  });

  const workspaces = useMemo(() => data || [], [data]);

  // Si el activo desaparece (borrado o sin acceso) o no hay ninguno
  // seleccionado, seleccionamos el primero visible automáticamente.
  useEffect(() => {
    if (!workspaces.length) return;
    if (!activeId || !workspaces.some((w) => w.id === activeId)) {
      setActiveId(workspaces[0].id);
    }
  }, [workspaces, activeId]);

  // Sync con localStorage
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (activeId) window.localStorage.setItem(LS_KEY, activeId);
    else window.localStorage.removeItem(LS_KEY);
  }, [activeId]);

  const setActiveWorkspace = useCallback((id: string | null) => {
    setActiveId(id);
  }, []);

  const activeWorkspace = useMemo(
    () => workspaces.find((w) => w.id === activeId) || null,
    [workspaces, activeId],
  );

  return (
    <WorkspaceContext.Provider
      value={{
        workspaces,
        isLoading,
        activeWorkspaceId: activeId,
        activeWorkspace,
        setActiveWorkspace,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error("useWorkspace debe usarse dentro de <WorkspaceProvider>");
  return ctx;
}
